import { inject } from '@angular/core';
import { patchState, signalStore, type, withMethods } from '@ngrx/signals';
import { upsertEntity, withEntities } from '@ngrx/signals/entities';
import { FeedbackService } from '@thiememeulenhoff/ng-api-gateway';
import { firstValueFrom } from 'rxjs';
import { ResultEntry } from '../interaction';
import {
  toRequestError,
  withRequestStatus,
} from '../_utils/request-status.feature';
import {
  convertFeedbackToQuestionFeedback,
  getTeacherFeedbackKey,
  prependArchivedMessage,
  QuestionFeedbackFilterable,
} from './feedback.utils';

type FeedbackEntry = {
  id: string;
  feedbacks: Array<QuestionFeedbackFilterable>;
};

type TeacherFeedbackInput = {
  result: ResultEntry | undefined;
  isSelfTest: boolean;
  actor: string;
  arrangementCode: string;
  contentPath: string;
};

const FeedbackStore = signalStore(
  { providedIn: 'root' },
  withEntities({ entity: type<FeedbackEntry>(), collection: 'teacher' }),
  withEntities({ entity: type<FeedbackEntry>(), collection: 'student' }),
  withRequestStatus(),
  withMethods((store, feedbackService = inject(FeedbackService)) => {
    return {
      getStudentFeedbackByQuestion(
        questionIdentifier: string,
      ): Array<QuestionFeedbackFilterable> {
        return (
          store.studentEntityMap()[questionIdentifier]?.feedbacks ?? []
        ).toSorted(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
      },

      getTeacherFeedbackByQuestionAndUser(
        contentPath: string,
        actor: string,
      ): Array<QuestionFeedbackFilterable> {
        const key = getTeacherFeedbackKey(contentPath, actor);
        return (store.teacherEntityMap()[key]?.feedbacks ?? []).toSorted(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
      },

      async fetchFeedbackByTeacher({
        result,
        isSelfTest,
        actor,
        arrangementCode,
        contentPath,
      }: TeacherFeedbackInput): Promise<void> {
        const key = getTeacherFeedbackKey(contentPath, actor);
        patchState(store, { requestStatus: 'loading' });

        try {
          const feedbacks = await firstValueFrom(
            feedbackService.getFeedbackByTeacher(
              actor,
              arrangementCode,
              contentPath,
            ),
          );

          patchState(
            store,
            upsertEntity(
              {
                id: key,
                feedbacks: prependArchivedMessage({
                  questionFeedbacks:
                    convertFeedbackToQuestionFeedback(feedbacks),
                  result,
                  isSelfTest,
                  actor,
                }),
              },
              { collection: 'teacher' },
            ),
            { requestStatus: 'success' },
          );
        } catch (error: unknown) {
          patchState(store, {
            requestStatus: toRequestError(error, 'Failed to fetch feedback'),
          });
        }
      },

      async fetchFeedbackByStudent(
        arrangementCode: string,
        contentPath: string,
      ): Promise<void> {
        patchState(store, { requestStatus: 'loading' });

        try {
          const feedbacks = await firstValueFrom(
            feedbackService.getFeedbackByStudent(arrangementCode, contentPath),
          );

          patchState(
            store,
            upsertEntity(
              {
                id: contentPath,
                feedbacks: convertFeedbackToQuestionFeedback(feedbacks),
              },
              { collection: 'student' },
            ),
            { requestStatus: 'success' },
          );
        } catch (error: unknown) {
          patchState(store, {
            requestStatus: toRequestError(error, 'Failed to fetch feedback'),
          });
        }
      },

      async postFeedback(
        actor: string,
        arrangementCode: string,
        contentPath: string,
        message: string,
      ): Promise<void> {
        patchState(store, { requestStatus: 'loading' });

        try {
          await firstValueFrom(
            feedbackService.postFeedback(
              actor,
              arrangementCode,
              contentPath,
              message,
            ),
          );
          patchState(store, { requestStatus: 'success' });
        } catch (error: unknown) {
          patchState(store, {
            requestStatus: toRequestError(error, 'Failed to post feedback'),
          });
          throw error;
        }
      },
    };
  }),
);
export default FeedbackStore;
